/**
 * Billing & Subscription Page — Company Admin
 * Route: /billing
 *
 * FIXES IN THIS VERSION:
 *
 * FIX 1 — Monthly subscriber can now switch to Yearly (same plan)
 *   - billingCycle toggle re-fetches plans with ?billing_cycle=yearly
 *     so backend recalculates proration correctly for the target cycle.
 *   - plan.is_current is now determined locally by checking BOTH
 *     plan.id === currentPlanId AND selectedCycle === currentDbCycle.
 *     Same plan + different cycle = button enabled, shows "Switch to Yearly".
 *   - action 'switch_cycle' handled: button label = "Switch to Yearly" or
 *     "Switch to Monthly", no proration alert needed if amount is 0.
 *
 * FIX 2 — Renewal now shows cycle selection before taking payment
 *   - "Renew Now" button opens RenewModal instead of immediately charging.
 *   - RenewModal shows current plan, monthly vs yearly price side-by-side,
 *     yearly savings badge, and lets user pick cycle before paying.
 *   - Works for both Razorpay and Stripe.
 *   - "Renew as Yearly" from Plans tab also now works via the same modal.
 *
 * FIX 3 — Button label bug fixed
 *   - Was showing "Downgrade to X" for upgrades with prorationDue=0.
 *   - Now uses action value directly for label, not prorationDue.
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
    Card, Row, Col, Button, Badge, Table,
    Modal, Form, Spinner, Alert, ProgressBar,
} from 'react-bootstrap';
import {
    CreditCard, RefreshCw, TrendingUp, TrendingDown,
    CheckCircle, AlertCircle, Clock, Zap, Shield,
    ArrowUpCircle, Calendar,
} from 'lucide-react';
import { toast } from 'react-toastify';
import axiosInstance from '../../api/axiosConfig';

const fmt = (n) => '₹' + parseFloat(n || 0).toLocaleString('en-IN',
    { minimumFractionDigits: 0, maximumFractionDigits: 0 });
const fmtDate = (d) => !d ? '—' : new Date(d).toLocaleDateString('en-IN',
    { day: 'numeric', month: 'long', year: 'numeric' });

const STATUS_CFG = {
    trial:     { bg: 'warning',   text: 'dark',  label: 'Trial',            icon: '⏳' },
    active:    { bg: 'success',   text: 'white', label: 'Active',           icon: '✅' },
    past_due:  { bg: 'danger',    text: 'white', label: 'Payment Overdue',  icon: '❌' },
    cancelled: { bg: 'secondary', text: 'white', label: 'Cancelled',        icon: '🚫' },
};

// ── Razorpay payment helper ───────────────────────────────────────────────
const openRazorpay = async (amount, planId, description, onSuccess) => {
    try {
        const orderRes = await axiosInstance.post('/billing/create-order', {
            amount, plan_id: planId, description,
        });
        const { order_id, key } = orderRes.data.data;
        const options = {
            key, amount: Math.round(amount * 100), currency: 'INR',
            name: 'PeoplesDock', description, order_id,
            handler: (response) => onSuccess(response),
            theme: { color: '#4f46e5' },
        };
        new window.Razorpay(options).open();
    } catch (err) {
        toast.error(err.response?.data?.message || 'Failed to initiate payment');
    }
};

// ════════════════════════════════════════════════════════════════════════════
// RENEW MODAL — shows cycle choice before taking payment
// ════════════════════════════════════════════════════════════════════════════
const RenewModal = ({ show, onHide, sub, selectedGW, gatewayCfg, onRenewed }) => {
    const [cycle,    setCycle]    = useState('monthly');
    const [loading,  setLoading]  = useState(false);

    // Pre-select current cycle when modal opens
    useEffect(() => {
        if (show) setCycle(sub?.billing_cycle || 'monthly');
    }, [show, sub]);

    if (!sub) return null;

    const monthlyPrice = parseFloat(sub.monthly_price || 0);
    const yearlyPrice  = parseFloat(sub.yearly_price  || 0);
    const yearlyMonthly = yearlyPrice / 12;  // monthly equivalent of yearly
    const savings      = monthlyPrice > 0
        ? Math.round(((monthlyPrice * 12 - yearlyPrice) / (monthlyPrice * 12)) * 100)
        : 0;
    const selectedPrice = cycle === 'yearly' ? yearlyPrice : monthlyPrice;

    const handlePay = async () => {
        setLoading(true);
        try {
            if (selectedGW === 'stripe') {
                const stripeRes = await axiosInstance.post('/payments/stripe/create-session', {
                    plan_id:      sub.plan_id,
                    billing_cycle: cycle,
                    amount:       selectedPrice,
                    description:  `Renew ${sub.plan_name} (${cycle})`,
                    success_url:  `${window.location.origin}/billing?payment=success&renew=1`,
                    cancel_url:   `${window.location.origin}/billing`,
                });
                window.location.href = stripeRes.data.data.checkout_url;
                return;
            }
            // Razorpay
            openRazorpay(
                selectedPrice,
                sub.plan_id,
                `Renew ${sub.plan_name} — ${cycle === 'yearly' ? 'Yearly' : 'Monthly'}`,
                async (razorpay) => {
                    try {
                        await axiosInstance.post('/billing/renew', {
                            billing_cycle:       cycle,
                            payment_confirmed:   true,
                            razorpay_payment_id: razorpay.razorpay_payment_id,
                        });
                        toast.success('Subscription renewed successfully!');
                        onHide();
                        onRenewed();
                    } catch (err) {
                        toast.error(err.response?.data?.message || 'Renewal failed');
                    }
                }
            );
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to initiate renewal');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal show={show} onHide={onHide} centered size="md">
            <Modal.Header closeButton className="border-0 pb-0">
                <Modal.Title className="fw-bold fs-5">
                    <RefreshCw size={18} className="me-2 text-primary"/>
                    Renew Subscription
                </Modal.Title>
            </Modal.Header>
            <Modal.Body className="px-4 pb-4">
                {/* Current plan info */}
                <div className="bg-light rounded-3 p-3 mb-4">
                    <div className="small text-muted fw-bold mb-1">CURRENT PLAN</div>
                    <div className="fw-bold fs-5">{sub.plan_name}</div>
                    <div className="text-muted small">
                        Expires: {fmtDate(sub.subscription_ends_at || sub.trial_ends_at)}
                        {sub.days_left > 0 && ` · ${sub.days_left} days left`}
                    </div>
                </div>

                {/* Cycle selector */}
                <div className="mb-4">
                    <div className="small fw-bold text-muted mb-3">CHOOSE BILLING CYCLE</div>
                    <Row className="g-3">
                        {/* Monthly option */}
                        <Col xs={6}>
                            <div
                                onClick={() => setCycle('monthly')}
                                style={{
                                    border: `2px solid ${cycle === 'monthly' ? '#4f46e5' : '#e5e7eb'}`,
                                    borderRadius: 12,
                                    padding: '16px 14px',
                                    cursor: 'pointer',
                                    background: cycle === 'monthly' ? '#f5f3ff' : '#fff',
                                    transition: 'all 0.15s',
                                    textAlign: 'center',
                                }}>
                                <div className="small text-muted fw-bold mb-1">MONTHLY</div>
                                <div style={{ fontSize: 22, fontWeight: 800, color: '#4f46e5' }}>
                                    {fmt(monthlyPrice)}
                                </div>
                                <div className="text-muted small">/month</div>
                                {cycle === 'monthly' && (
                                    <div className="mt-2">
                                        <CheckCircle size={16} color="#4f46e5"/>
                                    </div>
                                )}
                            </div>
                        </Col>
                        {/* Yearly option */}
                        <Col xs={6}>
                            <div
                                onClick={() => setCycle('yearly')}
                                style={{
                                    border: `2px solid ${cycle === 'yearly' ? '#059669' : '#e5e7eb'}`,
                                    borderRadius: 12,
                                    padding: '16px 14px',
                                    cursor: 'pointer',
                                    background: cycle === 'yearly' ? '#f0fdf4' : '#fff',
                                    transition: 'all 0.15s',
                                    textAlign: 'center',
                                    position: 'relative',
                                }}>
                                {savings > 0 && (
                                    <div style={{
                                        position: 'absolute', top: -10, left: '50%',
                                        transform: 'translateX(-50%)',
                                        background: '#059669', color: '#fff',
                                        fontSize: 10, fontWeight: 700,
                                        padding: '2px 10px', borderRadius: 20,
                                        whiteSpace: 'nowrap',
                                    }}>
                                        SAVE {savings}%
                                    </div>
                                )}
                                <div className="small text-muted fw-bold mb-1">YEARLY</div>
                                <div style={{ fontSize: 22, fontWeight: 800, color: '#059669' }}>
                                    {fmt(yearlyPrice)}
                                </div>
                                <div className="text-muted small">/year</div>
                                {yearlyMonthly > 0 && (
                                    <div style={{ fontSize: 11, color: '#059669', marginTop: 2 }}>
                                        {fmt(Math.round(yearlyMonthly))}/mo effective
                                    </div>
                                )}
                                {cycle === 'yearly' && (
                                    <div className="mt-2">
                                        <CheckCircle size={16} color="#059669"/>
                                    </div>
                                )}
                            </div>
                        </Col>
                    </Row>
                </div>

                {/* Summary */}
                <div className="bg-light rounded-3 p-3 mb-4">
                    <div className="d-flex justify-content-between align-items-center">
                        <div>
                            <div className="fw-bold">{sub.plan_name} — {cycle === 'yearly' ? 'Yearly' : 'Monthly'}</div>
                            <div className="text-muted small">
                                New expiry: {cycle === 'yearly'
                                    ? fmtDate(new Date(Date.now() + 365 * 86400000))
                                    : fmtDate(new Date(Date.now() + 30  * 86400000))}
                            </div>
                        </div>
                        <div style={{ fontSize: 22, fontWeight: 800, color: '#4f46e5' }}>
                            {fmt(selectedPrice)}
                        </div>
                    </div>
                </div>

                {/* Gateway selector — when both enabled */}
                {gatewayCfg.razorpay_enabled && gatewayCfg.stripe_enabled && (
                    <div className="mb-3">
                        <div className="small fw-bold text-muted mb-2">PAY VIA</div>
                        <div className="d-flex gap-2">
                            {[
                                { id: 'razorpay', label: 'Razorpay', sub: 'UPI / Cards', color: '#4f46e5' },
                                { id: 'stripe',   label: 'Stripe',   sub: 'Intl Cards',  color: '#6772e5' },
                            ].map(gw => (
                                <div key={gw.id}
                                    onClick={() => {/* gateway controlled by parent */}}
                                    style={{
                                        flex: 1, padding: '8px 12px', borderRadius: 10,
                                        border: selectedGW === gw.id ? `2px solid ${gw.color}` : '1px solid #e5e7eb',
                                        background: selectedGW === gw.id ? `${gw.color}10` : '#fff',
                                        textAlign: 'center', fontSize: 12, fontWeight: 600,
                                        opacity: selectedGW === gw.id ? 1 : 0.6,
                                    }}>
                                    <div>{gw.label}</div>
                                    <div style={{ fontSize: 10, color: '#94a3b8' }}>{gw.sub}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                <Button
                    variant="primary"
                    className="w-100 fw-bold rounded-pill"
                    style={{ padding: '12px 0' }}
                    onClick={handlePay}
                    disabled={loading || !selectedPrice}>
                    {loading ? <Spinner size="sm"/> : <>
                        Pay {fmt(selectedPrice)} &amp; Renew
                    </>}
                </Button>
            </Modal.Body>
        </Modal>
    );
};

// ════════════════════════════════════════════════════════════════════════════
// MAIN PAGE
// ════════════════════════════════════════════════════════════════════════════
const BillingPage = () => {
    const [sub,          setSub]          = useState(null);
    const [plans,        setPlans]        = useState([]);
    const [invoices,     setInvoices]     = useState([]);
    const [loading,      setLoading]      = useState(true);
    const [tab,          setTab]          = useState('overview');
    // selectedCycle: what the user has toggled in the Plans tab
    const [selectedCycle, setSelectedCycle] = useState('monthly');
    // currentDbCycle: what is actually stored in the DB for this company
    const [currentDbCycle, setCurrentDbCycle] = useState('monthly');
    const [currentPlanId,  setCurrentPlanId]  = useState(null);
    const [changing,     setChanging]     = useState(false);
    const [plansLoading, setPlansLoading] = useState(false);
    const [gatewayCfg,   setGatewayCfg]  = useState({ gateway: 'razorpay', razorpay_enabled: true, stripe_enabled: false });
    const [selectedGW,   setSelectedGW]  = useState('razorpay');
    const [showRenewModal, setShowRenewModal] = useState(false);

    useEffect(() => {
        axiosInstance.get('/superadmin/public/gateway-config').then(r => {
            const raw = r.data.data || {};
            const cfg = {
                gateway:          raw.active_gateway || raw.gateway || 'razorpay',
                razorpay_enabled: raw.razorpay_enabled === true || raw.razorpay_enabled === '1' || raw.razorpay_enabled === 1,
                stripe_enabled:   raw.stripe_enabled  === true || raw.stripe_enabled  === '1' || raw.stripe_enabled  === 1,
            };
            setGatewayCfg(cfg);
            setSelectedGW(!cfg.razorpay_enabled && cfg.stripe_enabled ? 'stripe' : cfg.gateway || 'razorpay');
        }).catch(() => {});
    }, []);

    // Load subscription + invoices (not plans — those are fetched separately)
    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [subRes, invRes] = await Promise.allSettled([
                axiosInstance.get('/billing/subscription'),
                axiosInstance.get('/billing/invoices'),
            ]);
            if (subRes.status === 'fulfilled') {
                const data = subRes.value.data.data;
                setSub(data);
                setCurrentDbCycle(data.billing_cycle || 'monthly');
                setSelectedCycle(data.billing_cycle || 'monthly');
                setCurrentPlanId(data.plan_id);
            }
            if (invRes.status === 'fulfilled') setInvoices(invRes.value.data.data || []);
        } catch { toast.error('Failed to load billing info'); }
        finally { setLoading(false); }
    }, []);

    useEffect(() => { load(); }, [load]);

    // Load plans — re-fetched whenever selectedCycle changes
    // Passes ?billing_cycle so backend calculates proration for the target cycle
    const loadPlans = useCallback(async (cycle) => {
        setPlansLoading(true);
        try {
            const res = await axiosInstance.get(`/billing/plans?billing_cycle=${cycle}`);
            const data = res.data.data;
            setPlans(data?.plans || []);
            // currentDbCycle from plans response is authoritative
            if (data?.current_billing_cycle) setCurrentDbCycle(data.current_billing_cycle);
            if (data?.current_plan_id)       setCurrentPlanId(data.current_plan_id);
        } catch { toast.error('Failed to load plans'); }
        finally { setPlansLoading(false); }
    }, []);

    // When Plans tab is opened or cycle toggle changes, re-fetch plans
    useEffect(() => {
        if (tab === 'plans') loadPlans(selectedCycle);
    }, [tab, selectedCycle, loadPlans]);

    // ── Change plan (or switch billing cycle) ─────────────────────────────
    const changePlan = async (plan, overrideProration) => {
        setChanging(true);
        try {
            const prorationDue = overrideProration ?? plan.proration_due ?? 0;

            const doApiChange = async (paymentData = {}) => {
                await axiosInstance.post('/billing/change-plan', {
                    new_plan_id:   plan.id,
                    billing_cycle: selectedCycle,
                    payment_confirmed: Object.keys(paymentData).length > 0,
                    ...paymentData,
                });
                toast.success(
                    plan.action === 'switch_cycle'
                        ? `Switched to ${selectedCycle} billing successfully!`
                        : plan.action === 'upgrade'
                        ? `Upgraded to ${plan.name} successfully!`
                        : `Plan changed to ${plan.name} successfully!`
                );
                load();
                loadPlans(selectedCycle);
            };

            if (prorationDue > 0) {
                setChanging(false);
                if (selectedGW === 'stripe') {
                    const stripeRes = await axiosInstance.post('/payments/stripe/create-session', {
                        plan_id:      plan.id,
                        billing_cycle: selectedCycle,
                        amount:       prorationDue,
                        description:  plan.action === 'switch_cycle'
                            ? `Switch to ${selectedCycle} billing — ${plan.name}`
                            : `${plan.action === 'upgrade' ? 'Upgrade' : 'Change'} to ${plan.name}`,
                        success_url:  `${window.location.origin}/billing?payment=success&plan=${plan.id}`,
                        cancel_url:   `${window.location.origin}/billing?tab=plans`,
                    });
                    window.location.href = stripeRes.data.data.checkout_url;
                } else {
                    openRazorpay(
                        prorationDue,
                        plan.id,
                        plan.action === 'switch_cycle'
                            ? `Switch to ${selectedCycle} — ${plan.name}`
                            : `${plan.action === 'upgrade' ? 'Upgrade' : 'Change'} to ${plan.name}`,
                        async (razorpay) => {
                            try {
                                await doApiChange({
                                    razorpay_payment_id: razorpay.razorpay_payment_id,
                                    razorpay_order_id:   razorpay.razorpay_order_id,
                                    razorpay_signature:  razorpay.razorpay_signature,
                                });
                            } catch (err) {
                                toast.error(err.response?.data?.message || 'Plan change failed');
                            }
                        }
                    );
                }
            } else {
                // No payment needed — apply immediately
                await doApiChange();
                setChanging(false);
            }
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to change plan');
            setChanging(false);
        }
    };

    if (loading) return (
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 400 }}>
            <Spinner animation="border" variant="primary"/>
        </div>
    );

    const statusCfg   = STATUS_CFG[sub?.subscription_status] || STATUS_CFG.trial;
    const daysLeft    = sub?.days_left || 0;
    const maxEmp      = sub?.max_employees || 999;
    const currEmp     = sub?.current_employees || 0;
    const empUsagePct = Math.min(100, Math.round((currEmp / maxEmp) * 100));

    return (
        <div className="p-2 p-md-4">
            {/* Header */}
            <div className="d-flex justify-content-between align-items-start mb-4 flex-wrap gap-3">
                <div>
                    <h4 className="fw-bold mb-1 d-flex align-items-center gap-2">
                        <CreditCard size={22} className="text-primary"/>
                        Billing & Subscription
                    </h4>
                    <p className="text-muted small mb-0">Manage your plan, renewals, and billing history</p>
                </div>
                <Button size="sm" variant="outline-secondary" onClick={load}>
                    <RefreshCw size={13}/>
                </Button>
            </div>

            {/* Expiry alert banner */}
            {sub?.is_expiring_soon && (
                <Alert variant="warning" className="d-flex align-items-center gap-3 mb-4">
                    <AlertCircle size={20}/>
                    <div className="flex-grow-1">
                        <strong>Your subscription expires in {daysLeft} day{daysLeft !== 1 ? 's' : ''}!</strong>
                        {' '}Renew now to avoid any interruption.
                    </div>
                    <Button size="sm" variant="warning" className="fw-bold"
                        onClick={() => setShowRenewModal(true)}>
                        Renew Now →
                    </Button>
                </Alert>
            )}
            {sub?.subscription_status === 'past_due' && (
                <Alert variant="danger" className="d-flex align-items-center gap-3 mb-4">
                    <AlertCircle size={20}/>
                    <div className="flex-grow-1">
                        <strong>Subscription expired!</strong> Renew immediately to restore full access.
                    </div>
                    <Button size="sm" variant="danger" className="fw-bold"
                        onClick={() => setShowRenewModal(true)}>
                        Renew Now →
                    </Button>
                </Alert>
            )}

            {/* Tab nav */}
            <div className="d-flex gap-2 mb-4">
                {[['overview', 'Overview'], ['plans', 'Change Plan'], ['invoices', 'Invoices']].map(([key, label]) => (
                    <Button key={key} size="sm"
                        variant={tab === key ? 'primary' : 'outline-secondary'}
                        onClick={() => setTab(key)}>
                        {label}
                    </Button>
                ))}
            </div>

            {/* ── OVERVIEW TAB ─────────────────────────────────────────── */}
            {tab === 'overview' && !sub && (
                <div className="text-center py-5">
                    <div style={{ fontSize: 48, marginBottom: 16 }}>📋</div>
                    <h6 className="fw-bold mb-2">No subscription data found</h6>
                    <p className="text-muted small mb-3">Choose a plan to get started.</p>
                    <Button variant="primary" className="rounded-pill px-4" onClick={() => setTab('plans')}>
                        Choose a Plan →
                    </Button>
                </div>
            )}

            {tab === 'overview' && sub && (
                <>
                    <Row className="g-4 mb-4">
                        <Col lg={8}>
                            <Card className="border-0 shadow-sm h-100">
                                <Card.Body className="p-4">
                                    <div className="d-flex justify-content-between align-items-start mb-4 flex-wrap gap-3">
                                        <div>
                                            <div className="text-muted small fw-bold text-uppercase mb-1">Current Plan</div>
                                            <h3 className="fw-bold mb-1">{sub.plan_name || 'Trial'}</h3>
                                            <div className="d-flex align-items-center gap-2 flex-wrap">
                                                <Badge bg={statusCfg.bg} text={statusCfg.text}>
                                                    {statusCfg.icon} {statusCfg.label}
                                                </Badge>
                                                <Badge bg="light" text="dark" style={{ fontSize: 11 }}>
                                                    <Calendar size={10} className="me-1"/>
                                                    {sub.billing_cycle === 'yearly' ? 'Yearly Billing' : 'Monthly Billing'}
                                                </Badge>
                                                <span className="text-muted small">{sub.status_message}</span>
                                            </div>
                                        </div>
                                        <div className="text-end">
                                            {sub.monthly_price && (
                                                <>
                                                    <div style={{ fontSize: 32, fontWeight: 800, color: '#4f46e5' }}>
                                                        {fmt(sub.billing_cycle === 'yearly' ? sub.yearly_price : sub.monthly_price)}
                                                    </div>
                                                    <div className="text-muted small">
                                                        {sub.billing_cycle === 'yearly' ? '/year' : '/mo'}
                                                    </div>
                                                    {sub.billing_cycle === 'monthly' && sub.yearly_price && (
                                                        <div className="text-success small mt-1" style={{ fontSize: 11 }}>
                                                            Switch to yearly &amp; save {
                                                                Math.round(((sub.monthly_price * 12 - sub.yearly_price) / (sub.monthly_price * 12)) * 100)
                                                            }%
                                                        </div>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    </div>

                                    {/* Expiry progress */}
                                    <div className="mb-4">
                                        <div className="d-flex justify-content-between small mb-1">
                                            <span className="fw-bold">Subscription Period</span>
                                            <span className="text-muted">
                                                Expires: {fmtDate(sub.subscription_ends_at || sub.trial_ends_at)}
                                            </span>
                                        </div>
                                        <ProgressBar
                                            now={Math.max(5, daysLeft > 30 ? 100 : (daysLeft / 30) * 100)}
                                            variant={daysLeft <= 3 ? 'danger' : daysLeft <= 7 ? 'warning' : 'success'}
                                            style={{ height: 8 }}/>
                                        <div className="text-muted small mt-1">
                                            {daysLeft > 0 ? `${daysLeft} days remaining` : 'Expired'}
                                        </div>
                                    </div>

                                    {/* Employee usage */}
                                    <div className="mb-4">
                                        <div className="d-flex justify-content-between small mb-1">
                                            <span className="fw-bold">Employee Usage</span>
                                            <span className="text-muted">{currEmp} / {sub.max_employees || '∞'}</span>
                                        </div>
                                        <ProgressBar
                                            now={empUsagePct}
                                            variant={empUsagePct >= 90 ? 'danger' : empUsagePct >= 75 ? 'warning' : 'primary'}
                                            style={{ height: 8 }}/>
                                        {empUsagePct >= 90 && (
                                            <div className="text-danger small mt-1">
                                                ⚠ Almost at limit — consider upgrading your plan
                                            </div>
                                        )}
                                    </div>

                                    <div className="d-flex gap-3 flex-wrap">
                                        <Button variant="primary" className="rounded-pill"
                                            onClick={() => setTab('plans')}>
                                            <TrendingUp size={14} className="me-1"/> Change Plan
                                        </Button>
                                        <Button variant="outline-primary" className="rounded-pill"
                                            onClick={() => setShowRenewModal(true)}>
                                            <RefreshCw size={14} className="me-1"/> Renew Subscription
                                        </Button>
                                        {/* Quick switch to yearly if currently monthly */}
                                        {sub.billing_cycle === 'monthly' && sub.yearly_price && (
                                            <Button variant="outline-success" className="rounded-pill"
                                                onClick={() => { setTab('plans'); setSelectedCycle('yearly'); }}>
                                                <ArrowUpCircle size={14} className="me-1"/>
                                                Switch to Yearly &amp; Save
                                            </Button>
                                        )}
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>

                        <Col lg={4}>
                            <div className="d-flex flex-column gap-3 h-100">
                                {[
                                    { label: 'Billing Cycle', value: sub.billing_cycle === 'yearly' ? 'Yearly (Save 20%)' : 'Monthly', icon: Clock,  color: '#4f46e5' },
                                    { label: 'Employees',     value: `${currEmp} active`,  icon: Shield, color: '#059669' },
                                    { label: 'Plan Features', value: `${(sub.features || []).length || '—'} features`, icon: Zap, color: '#f59e0b' },
                                ].map(({ label, value, icon: Icon, color }) => (
                                    <Card key={label} className="border-0 shadow-sm flex-grow-1">
                                        <Card.Body className="p-3 d-flex align-items-center gap-3">
                                            <div style={{
                                                width: 44, height: 44, borderRadius: 12,
                                                background: `${color}15`,
                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                flexShrink: 0,
                                            }}>
                                                <Icon size={20} color={color}/>
                                            </div>
                                            <div>
                                                <div className="text-muted small">{label}</div>
                                                <div className="fw-bold">{value}</div>
                                            </div>
                                        </Card.Body>
                                    </Card>
                                ))}
                            </div>
                        </Col>
                    </Row>

                    {sub.features && (
                        <Card className="border-0 shadow-sm">
                            <Card.Body className="p-4">
                                <h6 className="fw-bold mb-3">What's included in your plan</h6>
                                <Row className="g-2">
                                    {(typeof sub.features === 'string'
                                        ? JSON.parse(sub.features)
                                        : sub.features
                                    ).map((f, i) => (
                                        <Col xs={12} sm={6} md={4} key={i}>
                                            <div className="d-flex align-items-center gap-2 small">
                                                <CheckCircle size={14} color="#059669"/>
                                                <span>{f}</span>
                                            </div>
                                        </Col>
                                    ))}
                                </Row>
                            </Card.Body>
                        </Card>
                    )}
                </>
            )}

            {/* ── PLANS TAB ────────────────────────────────────────────── */}
            {tab === 'plans' && (
                <>
                    {/* Billing cycle toggle */}
                    <div className="d-flex align-items-center gap-3 mb-4 flex-wrap">
                        <div className="d-flex align-items-center gap-2 bg-white rounded-pill px-3 py-2 shadow-sm">
                            <span className="small fw-medium"
                                style={{ color: selectedCycle === 'monthly' ? '#4f46e5' : '#94a3b8' }}>
                                Monthly
                            </span>
                            <Form.Check type="switch"
                                checked={selectedCycle === 'yearly'}
                                onChange={() => setSelectedCycle(c => c === 'monthly' ? 'yearly' : 'monthly')}
                                className="mb-0"/>
                            <span className="small fw-medium"
                                style={{ color: selectedCycle === 'yearly' ? '#059669' : '#94a3b8' }}>
                                Yearly <Badge bg="success" style={{ fontSize: 9 }}>Save 20%</Badge>
                            </span>
                        </div>
                        {selectedCycle !== currentDbCycle && (
                            <Badge bg="info" text="dark" style={{ fontSize: 12 }}>
                                Showing {selectedCycle} pricing
                                {' — '}currently on {currentDbCycle} billing
                            </Badge>
                        )}
                        <span className="text-muted small">
                            {daysLeft > 0 && `${daysLeft} days remaining in current period`}
                        </span>
                    </div>

                    {plansLoading ? (
                        <div className="text-center py-5">
                            <Spinner animation="border" variant="primary"/>
                            <p className="text-muted small mt-2">Loading {selectedCycle} pricing…</p>
                        </div>
                    ) : plans.length === 0 ? (
                        <div className="text-center py-5">
                            <p className="text-muted">No plans found.</p>
                            <Button variant="outline-primary" size="sm" onClick={() => loadPlans(selectedCycle)}>Retry</Button>
                        </div>
                    ) : (
                        <Row className="g-4">
                            {plans.map(plan => {
                                const price = selectedCycle === 'yearly'
                                    ? plan.yearly_price : plan.monthly_price;

                                // FIX: is_current checks BOTH plan_id AND billing_cycle
                                const isCurrent = plan.id === currentPlanId
                                    && selectedCycle === currentDbCycle;

                                const isSamePlan = plan.id === currentPlanId;

                                // Determine action for this plan at selectedCycle
                                const currPlan  = plans.find(p => p.id === currentPlanId);
                                const currPrice = currPlan
                                    ? (selectedCycle === 'yearly' ? currPlan.yearly_price : currPlan.monthly_price)
                                    : 0;
                                const thisPrice = price ?? 0;

                                let action = isCurrent ? 'current'
                                    : isSamePlan && selectedCycle !== currentDbCycle ? 'switch_cycle'
                                    : thisPrice > currPrice ? 'upgrade'
                                    : thisPrice < currPrice ? 'downgrade'
                                    : 'switch';

                                const prorationDue = plan.proration_due || 0;

                                // ── Button label ──────────────────────────────
                                let btnLabel = '';
                                if (isCurrent)                           btnLabel = '✓ Current Plan';
                                else if (plan.monthly_price === null)    btnLabel = 'Contact Sales';
                                else if (action === 'switch_cycle')      btnLabel = `Switch to ${selectedCycle === 'yearly' ? 'Yearly' : 'Monthly'}${prorationDue > 0 ? ` — Pay ${fmt(prorationDue)}` : ''}`;
                                else if (action === 'upgrade')           btnLabel = prorationDue > 0 ? `Upgrade — Pay ${fmt(prorationDue)}` : `Upgrade to ${plan.name}`;
                                else if (action === 'downgrade')         btnLabel = `Downgrade to ${plan.name}`;
                                else                                     btnLabel = `Switch to ${plan.name}`;

                                // ── Button variant ────────────────────────────
                                const btnVariant = isCurrent              ? 'outline-secondary'
                                    : action === 'upgrade'                ? 'success'
                                    : action === 'switch_cycle' && selectedCycle === 'yearly' ? 'success'
                                    : 'primary';

                                return (
                                    <Col xs={12} md={4} key={plan.id}>
                                        <Card className="border-0 shadow-sm h-100" style={{
                                            borderRadius: 20,
                                            outline: isCurrent ? '2px solid #4f46e5' : 'none',
                                            background: isCurrent ? '#f8faff' : '#fff',
                                        }}>
                                            <Card.Body className="p-4">
                                                <div className="d-flex justify-content-between align-items-start mb-3">
                                                    <div>
                                                        <h5 className="fw-bold mb-1">{plan.name}</h5>
                                                        {price ? (
                                                            <div>
                                                                <span style={{ fontSize: 28, fontWeight: 800, color: '#4f46e5' }}>
                                                                    {fmt(price)}
                                                                </span>
                                                                <span className="text-muted small">
                                                                    {selectedCycle === 'yearly' ? '/yr' : '/mo'}
                                                                </span>
                                                            </div>
                                                        ) : (
                                                            <span style={{ fontSize: 22, fontWeight: 800 }}>Custom</span>
                                                        )}
                                                        {/* Show monthly equivalent for yearly */}
                                                        {selectedCycle === 'yearly' && price && (
                                                            <div style={{ fontSize: 12, color: '#059669' }}>
                                                                {fmt(Math.round(price / 12))}/mo effective
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div className="d-flex flex-column gap-1 align-items-end">
                                                        {isCurrent && <Badge bg="primary" style={{ fontSize: 11 }}>Current</Badge>}
                                                        {action === 'upgrade'      && <Badge bg="success" style={{ fontSize: 11 }}><TrendingUp size={10} className="me-1"/>Upgrade</Badge>}
                                                        {action === 'downgrade'    && <Badge bg="warning" text="dark" style={{ fontSize: 11 }}><TrendingDown size={10} className="me-1"/>Downgrade</Badge>}
                                                        {action === 'switch_cycle' && <Badge bg="info" text="dark" style={{ fontSize: 11 }}>Switch Cycle</Badge>}
                                                    </div>
                                                </div>

                                                {plan.max_employees && (
                                                    <div className="text-muted small mb-3">
                                                        Up to {plan.max_employees} employees
                                                    </div>
                                                )}

                                                {/* Same plan, different cycle info */}
                                                {action === 'switch_cycle' && (
                                                    <Alert variant={selectedCycle === 'yearly' ? 'success' : 'info'}
                                                        className="small py-2 mb-3">
                                                        {selectedCycle === 'yearly'
                                                            ? `💰 Switch to yearly and save ${
                                                                Math.round(((plan.monthly_price * 12 - plan.yearly_price) / (plan.monthly_price * 12)) * 100)
                                                            }% vs monthly`
                                                            : 'Switch to monthly — more flexible, billed every month'}
                                                        {prorationDue > 0 && (
                                                            <div className="mt-1 text-muted" style={{ fontSize: 11 }}>
                                                                Due today: {fmt(prorationDue)} (prorated for {plan.days_remaining} days)
                                                            </div>
                                                        )}
                                                        {prorationDue === 0 && (
                                                            <div className="mt-1 text-muted" style={{ fontSize: 11 }}>
                                                                Applied at next renewal — no charge today
                                                            </div>
                                                        )}
                                                    </Alert>
                                                )}

                                                {/* Proration info for upgrades */}
                                                {action === 'upgrade' && prorationDue > 0 && (
                                                    <Alert variant="info" className="small py-2 mb-3">
                                                        <strong>Due today: {fmt(prorationDue)}</strong>
                                                        <br/>
                                                        <span className="text-muted" style={{ fontSize: 11 }}>
                                                            Prorated for {plan.days_remaining} days remaining.
                                                            Credit: {fmt(plan.proration_credit)} applied.
                                                        </span>
                                                    </Alert>
                                                )}

                                                {action === 'downgrade' && (
                                                    <Alert variant="success" className="small py-2 mb-3">
                                                        No charge — applied immediately with credit
                                                    </Alert>
                                                )}

                                                {/* Features */}
                                                <div className="mb-4">
                                                    {(plan.features || []).map((f, i) => (
                                                        <div key={i} className="d-flex align-items-center gap-2 mb-1 small">
                                                            <CheckCircle size={12} color="#059669"/>
                                                            <span>{f}</span>
                                                        </div>
                                                    ))}
                                                </div>

                                                {/* Gateway selector — only for paid actions */}
                                                {!isCurrent && prorationDue > 0
                                                    && gatewayCfg.razorpay_enabled && gatewayCfg.stripe_enabled && (
                                                    <div className="mb-3">
                                                        <div className="small fw-bold text-muted mb-1">Pay via:</div>
                                                        <div className="d-flex gap-2">
                                                            {[
                                                                { id: 'razorpay', label: 'Razorpay', sub: 'UPI/Cards',  color: '#4f46e5' },
                                                                { id: 'stripe',   label: 'Stripe',   sub: 'Intl Cards', color: '#6772e5' },
                                                            ].map(gw => (
                                                                <div key={gw.id}
                                                                    onClick={() => setSelectedGW(gw.id)}
                                                                    style={{
                                                                        flex: 1, padding: '8px 10px', borderRadius: 10,
                                                                        border: selectedGW === gw.id ? `2px solid ${gw.color}` : '1px solid #e5e7eb',
                                                                        background: selectedGW === gw.id ? `${gw.color}08` : '#fff',
                                                                        cursor: 'pointer', textAlign: 'center',
                                                                        fontSize: 12, fontWeight: 600,
                                                                        transition: 'all 0.15s',
                                                                    }}>
                                                                    <div>{gw.label}</div>
                                                                    <div style={{ fontSize: 10, color: '#94a3b8', fontWeight: 400 }}>{gw.sub}</div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}

                                                <Button
                                                    variant={btnVariant}
                                                    className="w-100 rounded-pill fw-bold"
                                                    disabled={isCurrent || changing || plan.monthly_price === null}
                                                    onClick={() => changePlan({ ...plan, action }, prorationDue)}>
                                                    {changing ? <Spinner size="sm"/> : btnLabel}
                                                </Button>
                                            </Card.Body>
                                        </Card>
                                    </Col>
                                );
                            })}
                        </Row>
                    )}
                </>
            )}

            {/* ── INVOICES TAB ─────────────────────────────────────────── */}
            {tab === 'invoices' && (
                <Card className="border-0 shadow-sm">
                    {invoices.length === 0 ? (
                        <div className="text-center text-muted py-5">
                            <CreditCard size={36} className="mb-2 opacity-30"/>
                            <p>No billing transactions yet.</p>
                        </div>
                    ) : (
                        <Table hover responsive className="mb-0 align-middle small">
                            <thead className="bg-light">
                                <tr className="text-uppercase text-muted" style={{ fontSize: 10 }}>
                                    <th className="ps-4">Date</th>
                                    <th>Plan</th>
                                    <th>Cycle</th>
                                    <th>Description</th>
                                    <th>Amount</th>
                                    <th>Status</th>
                                    <th className="pe-4">Payment ID</th>
                                </tr>
                            </thead>
                            <tbody>
                                {invoices.map((inv, i) => (
                                    <tr key={i}>
                                        <td className="ps-4">{fmtDate(inv.payment_date || inv.created_at)}</td>
                                        <td className="fw-medium">{inv.plan_name || '—'}</td>
                                        <td>{inv.billing_cycle || '—'}</td>
                                        <td style={{ maxWidth: 200 }} className="text-muted">
                                            {(inv.description || '').slice(0, 60)}
                                            {inv.description?.length > 60 ? '…' : ''}
                                        </td>
                                        <td className="fw-bold">{fmt(inv.amount)}</td>
                                        <td>
                                            <Badge bg={
                                                inv.status === 'success' ? 'success' :
                                                inv.status === 'failed'  ? 'danger'  : 'secondary'
                                            } style={{ fontSize: 10 }}>
                                                {inv.status}
                                            </Badge>
                                        </td>
                                        <td className="pe-4 text-muted" style={{ fontSize: 11 }}>
                                            {inv.razorpay_payment_id || '—'}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    )}
                </Card>
            )}

            {/* Renew Modal */}
            <RenewModal
                show={showRenewModal}
                onHide={() => setShowRenewModal(false)}
                sub={sub}
                selectedGW={selectedGW}
                gatewayCfg={gatewayCfg}
                onRenewed={() => { load(); setShowRenewModal(false); }}
            />
        </div>
    );
};

export default BillingPage;
